"use client";

import { useEffect, useRef } from "react";

import { Box } from "@mantine/core";

import { AMBIENT_FIELD_MODE, type AmbientFieldMode } from "../constants";
import { createGravityFieldState, drawFrame } from "./gravity-field-engine";

interface AmbientFieldProps {
  mode: AmbientFieldMode;
  "aria-label": string;
}

/** Photo avatars for a handful of people. Order matters: the first three
    land on the power players, the decision-cluster centers. */
const AVATAR_SOURCES = [
  "/splash/interactive/neil.jpg",
  "/splash/interactive/maya.jpg",
  "/splash/interactive/anthony.jpg",
  "/splash/interactive/nancy.jpg",
  "/splash/interactive/kevin.jpg",
] as const;

/** Person indices that carry an avatar: the three power players (0..2)
    plus two regulars scattered in the crowd. Kept below the narrow-canvas
    population so mobile shows every face. */
const AVATAR_PERSON_INDICES = [0, 1, 2, 17, 29] as const;

/** Module scope: both figures share one set of decoded images. Built
    lazily so SSR never constructs an Image. */
let avatarsByPerson: (HTMLImageElement | undefined)[] | null = null;

function getAvatarsByPerson(): (HTMLImageElement | undefined)[] {
  if (!avatarsByPerson) {
    const sparse: (HTMLImageElement | undefined)[] = [];
    AVATAR_SOURCES.forEach((src, k) => {
      const image = new Image();
      image.src = src;
      sparse[AVATAR_PERSON_INDICES[k]] = image;
    });
    avatarsByPerson = sparse;
  }

  return avatarsByPerson;
}

/** How long the record figure takes to organize once it scrolls into view */
const SETTLE_DURATION_MS = 1600;
/** Hold on the gray mess first, so the settle reads as a transformation of
    the figure the reader already saw, not a different drawing */
const SETTLE_DELAY_MS = 600;
const DPR_CAP = 2;

function easeInOut(p: number): number {
  return p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;
}

/** A non-interactive, living render of the gravity-field engine at full
    scale. Two figures share this component; their identical composition is
    what lets the reader recognize one company, transformed. */
export default function AmbientField({ mode, "aria-label": ariaLabel }: AmbientFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const state = createGravityFieldState();
    const avatars = getAvatarsByPerson();
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const size = { width: 0, height: 0 };
    const restingF = mode === AMBIENT_FIELD_MODE.RECORD ? 1 : 0;

    let raf = 0;
    let lastNow: number | null = null;
    let visible = false;
    let hasSettled = false;
    let settleStart: number | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    let f = reducedMotion ? restingF : 0;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      const rect = canvas!.getBoundingClientRect();
      size.width = rect.width;
      size.height = rect.height;
      canvas!.width = rect.width * dpr;
      canvas!.height = rect.height * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function renderStatic() {
      drawFrame(ctx!, state, { ...size, now: 0, dt: 0, t: 1, f, avatars });
    }

    function tick(now: number) {
      if (lastNow === null) lastNow = now;
      const dt = Math.min(50, now - lastNow);
      lastNow = now;

      if (settleStart !== null) {
        const p = Math.min(1, (now - settleStart) / SETTLE_DURATION_MS);
        f = easeInOut(p);
        if (p >= 1) settleStart = null;
      }

      drawFrame(ctx!, state, { ...size, now, dt, t: 1, f, avatars });

      if (visible) raf = requestAnimationFrame(tick);
    }

    resize();
    renderStatic();

    if (reducedMotion) {
      // The animation loop would pick up late-loading photos on its next
      // frame; the frozen render needs an explicit repaint instead
      for (const image of avatars) {
        if (image && !image.complete) image.addEventListener("load", renderStatic, { once: true });
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = visible;
        visible = entry.isIntersecting;

        if (visible && !wasVisible) {
          lastNow = null;
          raf = requestAnimationFrame(tick);

          if (mode === AMBIENT_FIELD_MODE.RECORD && !hasSettled) {
            hasSettled = true;
            settleTimer = setTimeout(() => {
              settleStart = performance.now();
            }, SETTLE_DELAY_MS);
          }
        }

        if (!visible) cancelAnimationFrame(raf);
      },
      { threshold: 0.2 },
    );
    observer.observe(canvas);

    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 150);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settleTimer);
      clearTimeout(resizeTimer);
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [mode]);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      role="img"
      aria-label={ariaLabel}
      w="100%"
      h={{ base: 300, md: 360 }}
      style={{ display: "block" }}
    />
  );
}
